/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'

import { Badge, type BadgeProps } from '@/components/ui'

import type { ImportCommitResult, ImportOutcome } from './types'

import { AdminHeaderCells, AdminRowCells, type AdminColumns, useAdminTable } from '../adminTable'

const OUTCOME: Record<ImportOutcome, { label: string; variant: BadgeProps['variant'] }> = {
  created: { label: 'Created', variant: 'success' },
  granted: { label: 'Access given', variant: 'info' },
  skipped: { label: 'Skipped', variant: 'ghost' },
  failed: { label: 'Failed', variant: 'error' },
}

/** The results as a CSV — the only copy of the set-password links once the page is left. */
export const downloadResults = (results: ImportCommitResult[], fileName: string) => {
  const sheet = XLSX.utils.json_to_sheet(
    results.map((result) => ({
      email: result.email,
      outcome: result.outcome,
      emailSent: result.emailSent === undefined ? '' : result.emailSent ? 'yes' : 'no',
      setPasswordLink: result.setPasswordUrl ?? '',
      message: result.message ?? '',
    }))
  )
  const csv = XLSX.utils.sheet_to_csv(sheet)
  saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName)
}

const OUTCOME_ORDER = Object.keys(OUTCOME) as ImportOutcome[]

const COLUMNS: AdminColumns<ImportCommitResult> = [
  {
    id: 'row',
    header: '#',
    accessorFn: (result) => result.row,
    meta: { thClassName: 'w-10', className: 'text-base-content/50' },
    cell: ({ row }) => row.original.row + 1,
  },
  {
    id: 'result',
    header: 'Result',
    accessorFn: (result) => OUTCOME_ORDER.indexOf(result.outcome),
    cell: ({ row }) => (
      <Badge variant={OUTCOME[row.original.outcome].variant}>{OUTCOME[row.original.outcome].label}</Badge>
    ),
  },
  { id: 'email', header: 'Email', accessorFn: (result) => result.email },
  {
    id: 'emailed',
    header: 'Emailed',
    accessorFn: (result) => result.emailSent,
    cell: ({ row }) => (row.original.emailSent === undefined ? '' : row.original.emailSent ? 'Yes' : 'No'),
  },
  {
    id: 'notes',
    header: 'Notes',
    accessorFn: (result) => result.message || undefined,
    meta: { className: 'text-xs' },
  },
]

interface Props {
  results: ImportCommitResult[]
  sourceName: string
  onStartOver: () => void
}

export const ImportResults: React.FC<Props> = ({ results, sourceName, onStartOver }) => {
  const counts = results.reduce<Record<ImportOutcome, number>>(
    (acc, result) => ({ ...acc, [result.outcome]: acc[result.outcome] + 1 }),
    { created: 0, granted: 0, skipped: 0, failed: 0 }
  )
  const links = results.filter((result) => result.setPasswordUrl).length
  const unsent = results.filter((result) => result.setPasswordUrl && !result.emailSent).length
  const table = useAdminTable(results, COLUMNS, { getRowId: (result) => String(result.row) })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-base-100 p-4 shadow">
        <span className="text-sm">
          <strong>{counts.created}</strong> created · <strong>{counts.granted}</strong> given access ·{' '}
          <strong>{counts.skipped}</strong> skipped ·{' '}
          <strong className={counts.failed ? 'text-error' : ''}>{counts.failed}</strong> failed
        </span>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => downloadResults(results, `${sourceName.replace(/\.[^.]+$/, '')}-results.csv`)}
          >
            Download results{links ? ' with set-password links' : ''}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onStartOver}>
            Import another file
          </button>
        </div>
      </div>

      {unsent > 0 && (
        <div className="alert alert-warning text-sm">
          {unsent} new user{unsent === 1 ? '' : 's'} got no email. Download the results and send each their set-password
          link. Links expire after 7 days; after that they can request a new one from the sign-in page.
        </div>
      )}

      <div className="overflow-x-auto rounded-lg bg-base-100 shadow">
        <table className="table table-sm table-zebra">
          <thead>
            <tr>
              <AdminHeaderCells table={table} />
            </tr>
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} data-testid={`result-row-${row.original.row}`}>
                <AdminRowCells row={row} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
